"use client";

import {
  useEffect,
  useState,
} from "react";

type ThemeSetting =
  | "dark"
  | "light"
  | "system";

function resolveTheme(
  setting: ThemeSetting
) {
  if (setting === "system") {
    return window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches
      ? "dark"
      : "light";
  }

  return setting;
}

function applyTheme(
  setting: ThemeSetting
) {
  const resolved =
    resolveTheme(setting);

  document.documentElement.dataset.theme =
    resolved;

  document.documentElement.dataset.themeSetting =
    setting;

  localStorage.setItem(
    "typequest-theme",
    setting
  );
}

export default function ThemeToggle() {
  const [
    setting,
    setSetting,
  ] =
    useState<ThemeSetting>(
      "system"
    );

  const [
    open,
    setOpen,
  ] =
    useState(false);


  useEffect(() => {
    const saved =
      localStorage.getItem(
        "typequest-theme"
      );

    const initial:
      ThemeSetting =
        saved === "dark" ||
        saved === "light" ||
        saved === "system"
          ? saved
          : "system";

    setSetting(initial);
    applyTheme(initial);


    const media =
      window.matchMedia(
        "(prefers-color-scheme: dark)"
      );

    const handleSystemChange =
      () => {
        const current =
          localStorage.getItem(
            "typequest-theme"
          );

        if (
          !current ||
          current === "system"
        ) {
          document.documentElement.dataset.theme =
            media.matches
              ? "dark"
              : "light";
        }
      };

    media.addEventListener(
      "change",
      handleSystemChange
    );

    return () =>
      media.removeEventListener(
        "change",
        handleSystemChange
      );
  }, []);


  function selectTheme(
    value: ThemeSetting
  ) {
    setSetting(value);
    applyTheme(value);
    setOpen(false);
  }


  const currentIcon =
    setting === "light"
      ? "☀️"
      : setting === "dark"
      ? "🌙"
      : "💻";


  return (
    <div className="fixed bottom-5 right-5 z-[100]">

      {open && (
        <div className="theme-menu mb-2 w-44 overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 p-2 shadow-2xl">

          <button
            type="button"
            onClick={() =>
              selectTheme(
                "light"
              )
            }
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition hover:bg-slate-800 ${
              setting === "light"
                ? "text-cyan-300"
                : "text-slate-300"
            }`}
          >
            ☀️ Lys
          </button>

          <button
            type="button"
            onClick={() =>
              selectTheme(
                "dark"
              )
            }
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition hover:bg-slate-800 ${
              setting === "dark"
                ? "text-cyan-300"
                : "text-slate-300"
            }`}
          >
            🌙 Mørk
          </button>

          <button
            type="button"
            onClick={() =>
              selectTheme(
                "system"
              )
            }
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition hover:bg-slate-800 ${
              setting === "system"
                ? "text-cyan-300"
                : "text-slate-300"
            }`}
          >
            💻 System
          </button>

        </div>
      )}


      <button
        type="button"
        onClick={() =>
          setOpen(
            (current) =>
              !current
          )
        }
        aria-label="Velg tema"
        title="Velg tema"
        className="theme-toggle flex h-12 w-12 items-center justify-center rounded-full border border-slate-700 bg-slate-900 text-xl shadow-xl transition hover:scale-105 hover:border-cyan-500"
      >
        {currentIcon}
      </button>

    </div>
  );
}
